import { WORKSPACE_DOCUMENT_LIMITS, serializeContentDocument } from './document.mjs';
import { sajuBookOutput, sajuProductOf } from './saju-book-output.mjs';
import { runOutputJob } from './output-jobs.mjs';
import { getContentComponent } from './components.mjs';
import { rememberStoredAsset } from './stored-assets.mjs';
import { bookText } from './book-text.mjs';
import { requestSaju } from './component-saju.mjs';
import { NARRATIVE_COMPONENT } from './component-saju-narrative.mjs';
import { pageNarrativeComponents, pageYearBound, printedPerson } from './saju-narrative-import.mjs';
import { readBookChart } from './template-saju-facts.mjs';
import { FIGURE_FRAME_KEYS, bookFigures } from './template-saju-figures.mjs';
import { SAJU_BOOK_FRAME } from './template-saju-book.mjs';
import { loadPrintMetrics } from './print-wrap.mjs';

// The app charts a book prints as captures (canvas_scenes.py marks them) that a data component
// draws from the Saju app's calculation instead. A chart without one stays the captured picture.
const CHART_COMPONENTS = Object.freeze({ palja: 'saju.palja', relations: 'saju.relations', daeun: 'saju.daeun', seun: 'saju.seun',
  wolun: 'saju.wolun', 'seun-century': 'saju.seun-century' });
const BIRTH = ['birthDate', 'birthTime', 'unknownTime', 'isLunar', 'gender', 'yajasi'];

// The book's sample person, calculated for the year the book reads as now (its months, its current 대운).
function sampleSubject(subject, referenceYear) {
  if (!subject || typeof subject !== 'object' || Object.keys(subject).length !== BIRTH.length || BIRTH.some(key => !Object.hasOwn(subject, key))
    || !Number.isInteger(referenceYear)) {
    throw new Error('책의 표본 명식을 확인하지 못했습니다. 기존 원본을 유지합니다.');
  }
  return { ...subject, year: referenceYear };
}

/**
 * The frame words the book was built with where they differ from the Studio's, among those its figures fill ({ key: text }):
 * the Studio's pending edits of them (saju_book_workshop.py). A build that names no frame printed the Studio's own.
 */
function editedFigureFrame(frame) {
  if (frame === undefined) return {};
  if (!frame || typeof frame !== 'object' || Array.isArray(frame)) throw new Error('책이 쓴 틀 문구를 확인하지 못했습니다. 기존 원본을 유지합니다.');
  return Object.fromEntries(FIGURE_FRAME_KEYS.filter(key => typeof frame[key] === 'string' && frame[key] !== SAJU_BOOK_FRAME[key].text).map(key => [key, frame[key]]));
}

/**
 * A page's text boxes of the sample person's manuscripts and figures as narrative components (component-saju-narrative.mjs),
 * and the scene without the lines they draw instead — each component drawing exactly those lines (specs/011 §12).
 */
async function narrativeComponents(page, scene, context) {
  const frame = scene.elements.find(element => element.type === 'frame');
  const replaced = new Set(), components = [];
  for (const { props, snapshot, drawn, lineIds } of await pageNarrativeComponents({ ...page, scene }, context)) {
    lineIds.forEach(id => replaced.add(id));
    const instanceId = crypto.randomUUID();
    components.push({ instanceId, scene: drawn, record: { id: instanceId, type: NARRATIVE_COMPONENT.type, version: NARRATIVE_COMPONENT.version, props, snapshot },
      placement: { ...(frame ? { frameId: frame.id } : {}), x: 0, y: 0, scale: 1 } });
  }
  return { scene: { ...scene, elements: scene.elements.filter(element => !replaced.has(element.id)) }, components };
}

/** A page's scene without the captures its components replace, and those components placed where the captures were. */
async function chartComponents(page, subject, calculations) {
  const frame = page.scene.elements.find(element => element.type === 'frame');
  const charts = page.scene.elements.filter(element => element.type === 'image' && typeof element.chart === 'string' && Object.hasOwn(CHART_COMPONENTS, element.chart));
  const elements = page.scene.elements.filter(element => !charts.includes(element)).map(({ chart, ...element }) => element);
  const used = new Set(elements.filter(element => element.type === 'image').map(element => element.assetId));
  const scene = { ...page.scene, elements, assets: page.scene.assets.filter(asset => used.has(asset.id)) };
  const components = [];
  for (const chart of charts) {
    const plugin = getContentComponent(CHART_COMPONENTS[chart.chart]);
    const props = plugin.validateProps({ ...subject, ...Object.fromEntries(plugin.fields.map(field => [field.name, field.default])) });
    // One calculation per kind: every page of the book is the same person.
    if (!calculations.has(plugin.type)) calculations.set(plugin.type, await plugin.calculate(props));
    const snapshot = calculations.get(plugin.type), drawn = plugin.render(props, snapshot);
    const scale = Math.min(1, chart.width / drawn.width, chart.height / drawn.height);
    const instanceId = crypto.randomUUID();
    components.push({ instanceId, scene: drawn, record: { id: instanceId, type: plugin.type, version: plugin.version, props, snapshot },
      placement: { ...(chart.parentId === frame?.id ? { frameId: frame.id } : {}), x: chart.x, y: chart.y, scale } });
  }
  return { scene, components };
}

/** Where a book picture put in the asset store came from: the library hides these (a book has hundreds). */
export const SAJU_BOOK_PICTURE_SOURCE = 'saju-book';
const hex = buffer => Array.from(new Uint8Array(buffer), byte => byte.toString(16).padStart(2, '0')).join('');

// A book picture as the asset store records it. Everything but the dates follows from the picture itself, so putting the
// same picture again (another book, another import) is the same record: the store takes it without a conflict.
function bookPicture(id, asset, width, height) {
  const title = `사주 책 그림 ${id.slice(6, 14)}`;
  return { id, title, label: title, description: '사주 표본 책을 캔버스로 가져올 때 올린 그림입니다. 책의 쪽이 이 그림을 ID로 가리킵니다.',
    subject: '사주', topic: '사주 책', concepts: ['사주 책 그림'], recommendedUses: ['사주 책의 쪽'], avoidUses: ['사주 책이 아닌 수업'],
    keywords: ['사주', '책 그림'], role: 'scene', dimensions: { width, height }, mime: asset.mime, data: asset.data,
    provenance: { kind: 'imported', source: SAJU_BOOK_PICTURE_SOURCE, date: new Date().toISOString().slice(0, 10) },
    updatedAt: new Date().toISOString() };
}

/**
 * The book's pictures put in the Studio's asset store (four at a time), so its pages name them instead of holding them: a
 * 46-page book is then about 3 MiB rather than 10 and can be saved on the server (specs/012). Returns each page picture's
 * reference, or null when the store is not there or refuses one — the book then holds its pictures as before.
 */
async function storeBookPictures(scenes, store, onProgress) {
  if (!store?.sharedAssetsAvailable || !(await store.session()).authenticated) return null;
  const pictures = new Map();
  for (const scene of scenes) for (const asset of scene.assets) if (!pictures.has(asset.id)) pictures.set(asset.id, asset);
  const queue = [...pictures.values()], references = new Map();
  let failed = null, done = 0;
  async function next() {
    while (queue.length && !failed) {
      const asset = queue.shift();
      try {
        const bytes = Uint8Array.from(atob(asset.data), c => c.charCodeAt(0));
        const id = `local-${hex(await crypto.subtle.digest('SHA-256', bytes))}`;
        const blob = new Blob([bytes], { type: asset.mime });
        const image = await createImageBitmap(blob);
        const { width, height } = image;
        image.close();
        await store.registerAsset(bookPicture(id, asset, width, height));
        rememberStoredAsset(id, blob);
        references.set(asset.id, { id, mime: asset.mime, size: bytes.byteLength });
        onProgress(`책 그림을 자산 저장소에 올리고 있습니다 · ${++done} / ${pictures.size}`);
      } catch (error) { failed = error; }
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, queue.length) }, next));
  return failed ? null : references;
}

/** A page's scene naming its pictures by their place in the store. */
function namingStoredPictures(scene, references) {
  return { ...scene, assets: scene.assets.map(asset => references.get(asset.id)),
    elements: scene.elements.map(element => element.type === 'image' ? { ...element, assetId: references.get(element.assetId).id } : element) };
}

// Materialize the actual generated book once. From this point on its native
// snapshots own text, artwork and layout, just like every other canvas template.
// Never project free edits back into the legacy manuscript's limited text slots.
// The app charts it prints become data components of the book's sample person.
export async function importSajuCanvas(document, engine, onProgress = () => {}, { store = null, notice = () => {} } = {}) {
  const product = sajuProductOf(document);
  if (!product) return document;
  // The book is built by the local workshop (saju_book_workshop.py). Where it does not run — the
  // deployed Studio — the template opens as its own pages instead of failing to open.
  if (!await sajuBookOutput.workshop.available()) return document;
  onProgress('책의 글과 그림을 편집 가능한 캔버스로 준비하고 있습니다…');
  const result = await runOutputJob('/__saju/book/canvas', document, onProgress, {
    designEdits: document.outputDesigns?.['saju-book']?.edits ?? [],
  });
  if (result.productId !== product.id || !Array.isArray(result.pages) || !result.pages.length ||
      result.pages.length > WORKSPACE_DOCUMENT_LIMITS.sections) {
    throw new Error('책의 캔버스 페이지를 확인하지 못했습니다. 기존 원본을 유지합니다.');
  }
  const subject = sampleSubject(result.subject, result.referenceYear);
  // The sample person's book chart, once: the figures every page prints and the facts its manuscripts were written from.
  const [chart] = await Promise.all([requestSaju('chart', subject), loadPrintMetrics()]);
  const book = readBookChart(chart), person = printedPerson(result.pages), frame = editedFigureFrame(result.frame);
  const words = { ...SAJU_BOOK_FRAME, ...Object.fromEntries(Object.entries(frame).map(([key, text]) => [key, { ...SAJU_BOOK_FRAME[key], text }])) };
  const narrative = { product: product.id, subject, book, person, frame, hashes: new Map(),
    figures: bookFigures(book, { frame: words, year: subject.year, ...person }) };
  const calculations = new Map(), prepared = [];
  for (const [index, page] of result.pages.entries()) {
    if (page.number !== index + 1 || typeof page.title !== 'string' || !page.scene) {
      throw new Error('책의 캔버스 페이지 순서가 올바르지 않습니다.');
    }
    const charts = await chartComponents(page, subject, calculations);
    const texts = await narrativeComponents(page, charts.scene, narrative);
    prepared.push({ page, scene: texts.scene, components: [...charts.components, ...texts.components] });
  }
  // Only the pictures the pages keep: a chart capture a data component replaces is not put in the store.
  const references = await storeBookPictures(prepared.map(({ scene }) => scene), store, onProgress);
  const sections = [];
  for (const [index, { page, scene, components }] of prepared.entries()) {
    // Every page's printed text was written for the book's sample person: a page records whom (book-text.mjs).
    const source = await engine.fromScene(references ? namingStoredPictures(scene, references) : scene,
      { components, bookText: bookText(product.id, subject, pageYearBound(page, subject.year)) });
    sections.push({ id: crypto.randomUUID(), kind: 'canvas', title: page.title, ...source });
    onProgress(`편집 가능한 캔버스를 준비하고 있습니다 · ${index + 1} / ${result.pages.length}쪽${calculations.size ? ` · 계산 컴포넌트 ${calculations.size}종` : ''}`);
  }
  const next = { schemaVersion: 2, format: 'blocksuite', title: document.title, sections };
  serializeContentDocument(next);
  // After the page-by-page progress, which would cover it.
  if (!references) notice('책 그림을 문서 안에 넣었습니다. 로그인한 뒤 다시 가져오면 그림을 자산 저장소에 두어, 서버에 저장할 수 있을 만큼 문서가 작아집니다.');
  return next;
}
